import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useCanAccessCamera, useCanAccessMicrophone, useIsSharingScreen, useIsUsingFrontCamera, useMediaStream, useRequestMediaPermission, useSetDisplaySurface, } from '../../state/mediaState';
import { useScheduledBroadcast } from '../../state/scheduleState';
import { isSupportedBrowser, useHasSupportedVideoCodec } from '../../utils';
import { useBroadcastUpdater } from '../../utils/media';
import { getStreamUrl } from '../../utils/url';
import { useStreamrTvBroadcaster } from '../../utils/useStreamrTvBroadcaster';
import { Anchor } from '../Anchor';
import { CodecNotSupported, GenericBrowserNotSupported, } from '../BrowserNotSupported';
import { Button } from '../Button';
import { AudioDeviceButton, VideoDeviceButton } from '../DeviceButton';
import { ScreenShareIcon } from '../Icon';
import { LiveIndicator } from '../LiveIndicator';
import { LoadingIndicator } from '../LoadingIndicator';
import { Stage } from '../Stage';
import SvgIcon from '../SvgIcon';
import { Video } from '../Video';
import { showBroadcastPopup } from '../popups/BroadcastPopup';
import { selectShareScreenSource } from '../popups/ShareScreenPopup';
import { BlockedPermissions, BottomViewToolbar, StageContainer, StatusContainer, TopViewToolbar, ViewRoot, ViewToolbarCopyUrlButton, ViewToolbarSegment, ViewToolbarShareButtons, ViewToolbarShareUrlButton, } from './View';
export function BroadcasterView({ streamPartId }) {
    const stageControllerRef = useRef(null);
    const broadcaster = useStreamrTvBroadcaster(streamPartId);
    const mediaStream = useMediaStream();
    const [broadcastStartedAt, setBroadcastStartedAt] = useState(undefined);
    const isBroadcasting = broadcastStartedAt !== undefined;
    useBroadcastUpdater({
        broadcaster,
        mediaStream,
        enabled: isBroadcasting,
    });
    const shareScreenButtonAnchorRef = useRef(null);
    const setDisplaySurface = useSetDisplaySurface();
    const isSharingScreen = useIsSharingScreen(mediaStream);
    const isUsingFrontCamera = useIsUsingFrontCamera(mediaStream);
    const [isShareScreenPopupOpen, setIsShareScreenPopupOpen] = useState(false);
    const [showSidebar, setShowSidebar] = useState(false);
    const browserVideoCodecSupported = useHasSupportedVideoCodec();
    useEffect(() => {
        if (showSidebar) {
            stageControllerRef.current?.shrink();
        }
        else {
            stageControllerRef.current?.unshrink();
        }
    }, [showSidebar]);
    const goLiveButtonAnchorRef = useRef(null);
    const broadcast = useScheduledBroadcast(streamPartId);
    const canAccessCamera = useCanAccessCamera();
    const canAccessMicrophone = useCanAccessMicrophone();
    const requestMediaPermission = useRequestMediaPermission();
    const hasTracks = useMemo(() => mediaStream.getTracks().length > 0, [mediaStream]);
    const canGoLive = hasTracks && !!broadcaster;
    if (!isSupportedBrowser()) {
        return _jsx(GenericBrowserNotSupported, {});
    }
    if (browserVideoCodecSupported === false) {
        return _jsx(CodecNotSupported, {});
    }
    const url = getStreamUrl(streamPartId);
    return (_jsxs(_Fragment, { children: [_jsx(LoadingIndicator, { loading: !broadcaster }), _jsxs(ViewRoot, { children: [_jsxs(TopViewToolbar, { children: [_jsx(StatusContainer, { children: _jsx(LiveIndicator, { startedAt: broadcastStartedAt, streamPartId: streamPartId }) }), _jsxs(ViewToolbarShareButtons, { children: [_jsx(ViewToolbarCopyUrlButton, { url: url }), _jsx(ViewToolbarShareUrlButton, { url: url })] })] }), _jsx(StageContainer, { children: _jsx(Stage, { controllerRef: stageControllerRef, children: canAccessCamera ||
                                canAccessMicrophone ||
                                isSharingScreen ? (_jsx(Video, { fit: "contain", srcObject: mediaStream, autoPlay: true, playsInline: true, muted: true, mirrored: isUsingFrontCamera })) : (_jsx(BlockedPermissions, { children: _jsx(Button, { type: "button", kind: "secondary", size: "big", padding: "normal", onClick: () => {
                                        requestMediaPermission('both');
                                    }, children: "Allow microphone and camera" }) })) }) }), _jsxs(BottomViewToolbar, { children: [_jsxs(Segment0, { children: [_jsxs(BroadcastTitle, { children: ["\u200C", broadcast?.name] }), _jsx("div", {}), _jsx(GoLiveButton, { type: "button", kind: isBroadcasting ? 'destructive' : 'primary', shape: "pill", onClick: () => {
                                            setBroadcastStartedAt((prev) => prev ? undefined : new Date());
                                        }, children: isBroadcasting ? 'End' : 'Go Live' })] }), _jsxs(Segment1, { children: [_jsxs(BroadcastTitle, { children: ["\u200C", broadcast?.name] }), _jsx(AudioDeviceButton, { mediaStream: mediaStream }), _jsx(VideoDeviceButton, {}), _jsx(ShareScreenButtonAnchor, { ref: shareScreenButtonAnchorRef, children: isSharingScreen ? (_jsx(ShareScreenButton, { type: "button", shape: "pill", onClick: () => {
                                                setDisplaySurface(undefined);
                                            }, children: "Stop sharing" })) : (_jsx(ShareScreenButton, { type: "button", shape: "pill", padding: "none", kind: isShareScreenPopupOpen
                                                ? undefined
                                                : 'secondary', variant: isShareScreenPopupOpen
                                                ? undefined
                                                : 'dark', icon: _jsx(ScreenShareIcon, { active: isShareScreenPopupOpen }), onClick: () => {
                                                selectShareScreenSource({
                                                    anchorRef: shareScreenButtonAnchorRef,
                                                    onOpenPopup: () => {
                                                        setIsShareScreenPopupOpen(true);
                                                    },
                                                    onClosePopup: () => {
                                                        setIsShareScreenPopupOpen(false);
                                                    },
                                                });
                                            } })) }), _jsx(GoLiveButton, { type: "button", kind: isBroadcasting ? 'destructive' : 'primary', shape: "pill", onClick: () => {
                                            setBroadcastStartedAt((prev) => prev ? undefined : new Date());
                                        }, disabled: !canGoLive, children: isBroadcasting ? 'End' : 'Go Live' }), _jsx(SettingsButtonWrap, { children: _jsx(Anchor, { ref: goLiveButtonAnchorRef, children: _jsx(Button, { type: "button", kind: "secondary", shape: "pill", padding: "none", variant: "dark", icon: _jsx(SvgIcon, { name: "dotDotDot" }), onClick: () => {
                                                    showBroadcastPopup({
                                                        streamPartId,
                                                        anchorRef: goLiveButtonAnchorRef,
                                                        onBeforeOpen: () => {
                                                            setShowSidebar(true);
                                                        },
                                                        onDismiss: () => {
                                                            setShowSidebar(false);
                                                        },
                                                        initialQuality: broadcaster?.getVideoEncodingQuality() ??
                                                            'sd',
                                                        onVideoQualityChanged: (quality) => {
                                                            broadcaster?.setVideoEncodingQuality(quality);
                                                        },
                                                    });
                                                } }) }) })] })] })] })] }));
}
const BroadcastTitle = styled.div `
    font-weight: 700;
    font-size: 18px;
    line-height: 18px;
    letter-spacing: 0%;
    color: #FFFFFF;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;
const ShareScreenButtonAnchor = styled(Anchor) ``;
const GoLiveButton = styled(Button) ``;
const ShareScreenButton = styled(Button) ``;
const SettingsButtonWrap = styled.div `
    display: flex;
    justify-content: flex-end;
`;
const Segment0 = styled(ViewToolbarSegment) `
    gap: 0.5rem;
    grid-template-columns: auto 1fr auto;
    margin-bottom: 1.5rem;

    @media (min-width: 48rem) {
        display: none;
    }
`;
const Segment1 = styled(ViewToolbarSegment) `
    grid-template-columns: auto auto auto;

    ${BroadcastTitle} {
        display: none;
    }

    ${GoLiveButton} {
        display: none;
    }

    ${ShareScreenButtonAnchor} {
        display: none;
    }

    @media (min-width: 22rem) {
        grid-template-columns: auto auto auto auto;

        ${ShareScreenButtonAnchor} {
            display: block;
        }
    }

    @media (min-width: 48rem) {
        grid-template-columns: 1fr auto auto auto auto 1fr;

        ${BroadcastTitle} {
            display: block;
        }

        ${GoLiveButton} {
            display: flex;
        }
    }
`;
